import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import { CloudUpload, Download, Description } from '@mui/icons-material';
import { useOktaAuth } from '@okta/okta-react';
import { toast } from 'react-toastify';
import { processSetonPipeMarkersFile } from '../providers/productFinderAPI';
import CelebrateButton from '../components/CelebrateButton';
import { SetonPipeMarkerProcessingResponse } from '../models/iProductSearchRequest';

interface FileWithPreview extends File {
  preview?: string;
}

const SetonPipeMarkersPage: React.FC = () => {
  const { authState } = useOktaAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedFile, setSelectedFile] = useState<FileWithPreview | null>(null);
  const [processing, setProcessing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [processingResult, setProcessingResult] = useState<SetonPipeMarkerProcessingResponse | null>(null);

  // Utility function to truncate long filenames
  const truncateFilename = (filename: string, maxLength: number = 50): string => {
    if (filename.length <= maxLength) return filename;

    const extension = filename.split('.').pop();
    const nameWithoutExtension = filename.substring(0, filename.lastIndexOf('.'));
    const maxNameLength = maxLength - (extension ? extension.length + 4 : 3); // Account for "..." and "."

    if (nameWithoutExtension.length <= maxNameLength) return filename;

    return `${nameWithoutExtension.substring(0, maxNameLength)}...${extension ? '.' + extension : ''}`;
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];

    // Validate file type
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select an Excel file (.xlsx or .xls)');
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setSelectedFile(file);
    // Clear previous processing results when a new file is selected
    setProcessingResult(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
  };

  const processFile = async () => {
    if (!selectedFile || !authState?.accessToken?.accessToken) {
      toast.error('No file selected or authentication token missing');
      return;
    }

    setProcessing(true);
    setProcessingResult(null);
    try {
      const result = await processSetonPipeMarkersFile(authState.accessToken.accessToken, selectedFile);

      setProcessingResult(result);
      setSelectedFile(null);
    } catch (error) {
      toast.error('Error processing file. Please check the format and try again.');
    } finally {
      setProcessing(false);
    }
  };

  const downloadFile = () => {
    if (!processingResult) return;

    // Convert base64 to blob
    const byteCharacters = atob(processingResult.fileContentBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = processingResult.fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const clearFile = () => {
    setSelectedFile(null);
    setProcessingResult(null);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Seton Take Off (Pipe Markers)
      </Typography>
      <Grid container spacing={4}>
        {/* Instructions on the left */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Typography variant="h6" gutterBottom>
            Instructions
          </Typography>

          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
            Upload an Excel file containing Pipe Marker data. The system will search for matching products and add
            YNumber and Catalog columns to your file.
          </Typography>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>File Requirements:</strong>
              <br />
              • Excel file with "Setmark" and/or "Opticode" and/or "Ultramark" sections
              <br />
              • Columns should include Legend, Size, and Color information
              <br />• The system will add YNumber and Catalog columns with matching products
            </Typography>
          </Alert>
        </Grid>

        {/* File processing on the right */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              mb: 3,
              border: dragActive ? `2px dashed ${theme.palette.primary.main}` : '2px dashed #ccc',
              backgroundColor: dragActive ? theme.palette.action.hover : 'transparent',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => document.getElementById('file-input')?.click()}
          >
            <input
              id="file-input"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />

            <Grid container direction="column" alignItems="center" spacing={2}>
              <Grid>
                <CloudUpload sx={{ fontSize: 48, color: 'text.secondary' }} />
              </Grid>
              <Grid>
                <Tooltip title={selectedFile?.name || ''} placement="top">
                  <Typography variant="h6" align="center">
                    {selectedFile
                      ? (() => {
                          const maxLength = isMobile ? 30 : 50;
                          return truncateFilename(selectedFile.name, maxLength);
                        })()
                      : 'Drop your Excel file here or click to browse'}
                  </Typography>
                </Tooltip>
              </Grid>
              <Grid>
                <Typography variant="body2" color="text.secondary" align="center">
                  Supports .xlsx and .xls files (max 10MB)
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {selectedFile && !processingResult && (
            <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: theme.palette.grey[50] }}>
              <Grid container alignItems="center" spacing={2}>
                <Grid>
                  <Description color="primary" />
                </Grid>
                <Grid size="grow">
                  <Tooltip title={selectedFile.name} placement="top">
                    <Typography
                      variant="body1"
                      fontWeight="medium"
                      sx={{
                        wordBreak: 'break-all',
                        overflowWrap: 'break-word',
                        maxWidth: '100%',
                        cursor: 'help',
                      }}
                    >
                      {truncateFilename(selectedFile.name, isMobile ? 25 : 40)}
                    </Typography>
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </Grid>
                <Grid>
                  <Button variant="outlined" size="small" onClick={clearFile} disabled={processing}>
                    Remove
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          )}

          {!processingResult && (
            <Grid container spacing={2} justifyContent={isMobile ? 'center' : 'flex-start'} sx={{ mb: 3 }}>
              <Grid>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={processing ? <CircularProgress size={20} /> : <Download />}
                  onClick={processFile}
                  disabled={!selectedFile || processing}
                  sx={{ minWidth: 200 }}
                >
                  {processing ? 'Processing...' : 'Process File'}
                </Button>
              </Grid>
            </Grid>
          )}

          {processingResult && (
            <Paper
              elevation={2}
              sx={{
                p: 3,
                border: `2px solid ${theme.palette.success.main}`,
                backgroundColor: 'white',
                color: 'black',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'black' }}>
                  <Description />
                  Processing Complete!
                </Typography>

                <CelebrateButton />
              </Box>

              <Typography variant="body1" sx={{ mb: 2, color: 'black' }}>
                {`File processed successfully. ${processingResult.foundResults} matches found out of ${processingResult.processedRows} rows processed.`}
              </Typography>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid
                  size={{
                    xs: 6,
                    sm: 3
                  }}>
                  <Typography variant="caption" display="block" sx={{ color: 'black' }}>
                    Processed Rows
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'black' }}>
                    {processingResult.processedRows}
                  </Typography>
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    sm: 3
                  }}>
                  <Typography variant="caption" display="block" sx={{ color: 'black' }}>
                    Found Results
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {processingResult.foundResults}
                  </Typography>
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    sm: 3
                  }}>
                  <Typography variant="caption" display="block" sx={{ color: 'black' }}>
                    Custom
                  </Typography>
                  <Typography variant="h6" color="warning.main">
                    {processingResult.notFoundResults}
                  </Typography>
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    sm: 3
                  }}>
                  <Typography variant="caption" display="block" sx={{ color: 'black' }}>
                    Errors
                  </Typography>
                  <Typography variant="h6" color="error.main">
                    {processingResult.errorRows}
                  </Typography>
                </Grid>
              </Grid>

              {processingResult.processingErrors.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="medium">
                    Processing Errors:
                  </Typography>
                  <ul style={{ margin: 0 }}>
                    {processingResult.processingErrors.map((error) => (
                      <li key={error}>{error}</li>
                    ))}
                  </ul>
                </Alert>
              )}

              <Button
                variant="contained"
                size="large"
                startIcon={<Download />}
                onClick={downloadFile}
                sx={{ minWidth: 200, backgroundColor: theme.palette.primary.main }}
              >
                Download Processed File
              </Button>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default SetonPipeMarkersPage;
